'use client';

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { UserSupportTicketCategory } from '@trucksmarter/shared';
import DallasSentimentRating, {
  Sentiment,
} from '../Dallas/Forms/DallasSentimentRatingField/DallasSentimentRatingField';
import DallasButton from '~/app/_ui/Dallas/Buttons/DallasButton/DallasButton';
import DallasForm from '~/app/_ui/Dallas/Forms/DallasForm';
import DallasStack from '~/app/_ui/Dallas/Stack/DallasStack';
import DallasTextArea from '../Dallas/Forms/DallasTextArea/DallasTextArea';
import DallasSpinner from '../Dallas/ProgressIndicators/Spinner/DallasSpinner';
import useTrackAnalyticsEventFeedback from '@lib/analytics/useTrackAnalyticsEventFeedback';
import createSupportTicket from '@lib/api/client/create-support-ticket';
import DallasText from '../Dallas/Text/DallasText';
import { captureException } from '../../../services/sentry.service';

const SENTIMENT_LABELS = Object.freeze({
  [Sentiment.VerySatisfied]: 'I love it!',
  [Sentiment.Satisfied]: 'Yes',
  [Sentiment.Neutral]: "It's okay",
  [Sentiment.Dissatisfied]: 'No',
  [Sentiment.VeryDissatisfied]: 'Not at all',
});

export interface FeedbackFormValues {
  comments: string;
  sentiment: Sentiment | null;
}

interface Props {
  className?: string;
  onSubmitted: (success: boolean) => void;
}

export default function FeedbackForm({ className, onSubmitted }: Props) {
  const track = useTrackAnalyticsEventFeedback();
  const form = useForm<FeedbackFormValues>({
    defaultValues: {
      comments: '',
      sentiment: null,
    },
  });
  const {
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    form.setFocus('comments');
  }, [form]);

  return (
    <DallasForm
      className={className}
      form={form}
      onSubmit={async ({ sentiment, comments }) => {
        try {
          track({
            type: 'FEEDBACK',
            metadata: {
              comments,
              sentiment,
            },
          });
          const { isSuccess } = await createSupportTicket(
            UserSupportTicketCategory.FeedbackWeb,
            `Sentiment: ${sentiment ?? 'N/A'} | Comments: ${comments}`,
          );
          onSubmitted(isSuccess);
        } catch (error) {
          captureException(error);
          onSubmitted(false);
        }
      }}
    >
      <DallasStack gap="md" alignHorizontal="center">
        <DallasText variant="headerRegularS" align="center">
          Are you liking TruckSmarter?
        </DallasText>
        <DallasSentimentRating
          sentimentLabels={SENTIMENT_LABELS}
          name="sentiment"
          disabled={isSubmitting}
          required
        />
        <DallasTextArea
          name="comments"
          placeholder="We want to hear from you! What's working and how could we improve?"
          rows={7}
          disabled={isSubmitting}
          required
        />
        <DallasButton
          color="primary"
          label="Submit feedback"
          addOnStart={isSubmitting ? <DallasSpinner /> : undefined}
          disabled={isSubmitting}
          submit
        />
      </DallasStack>
    </DallasForm>
  );
}
